'use client'

// Starts the Free Ads Account Audit. The audit is an ordinary conversation in
// the brand the visitor connected an ad account to, so the page never runs it
// itself:
// 1. POST /api/free-ads-audit/start spends the one-shot audit and returns the
//    server-authored audit request plus the brand chat's coordinates.
// 2. The request is staged in the pending-prompt store, scoped to that brand
//    and person, so the brand's new-chat page sends it as the first message —
//    on whichever runtime the brand runs, with that chat's own streaming,
//    tools, approvals and billing.
// 3. The audit org is activated (the session claim lags a just-created org)
//    and the browser opens the brand's new chat.

import { useCallback, useRef, useState } from 'react'

import { ApiFetchError } from '@/hooks/api-fetcher'
import { useApiRequest } from '@/hooks/use-api'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  takeFreeAdsAuditRunIntent,
  type FreeAdsAuditPlatform,
} from '@/hooks/use-free-ads-audit-connect'
import { newIdempotencyKey } from '@/lib/idempotency'
import { buildBrandUrl } from '@/lib/routes'
import { usePendingPromptStore } from '@/stores/pending-prompt-store'

interface StartAuditResponse {
  prompt: string
  orgSlug: string
  authOrgId: string
  brandId: string
  brandSlug: string
}

export interface FreeAdsAuditStartInput {
  brandId: string
  platform: FreeAdsAuditPlatform
  websiteUrl?: string
}

/** Map a failed start to the page's error copy key. */
export function freeAdsAuditStartErrorCode(error: unknown): string {
  if (error instanceof ApiFetchError) {
    if (error.statusCode === 429) {
      return error.code === 'global' ? 'global_limit' : 'already_used'
    }
    if (error.statusCode === 404) return 'brand_not_found'
  }
  return 'audit_unavailable'
}

export function useFreeAdsAuditStart() {
  const { user } = useAuthContext()
  // The page renders its own error line; no global toast on top of it.
  const { request } = useApiRequest({ suppressGlobalErrorHandler: true })
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // `starting` lags a render behind; a ref gates a second submit out at once.
  const inFlightRef = useRef(false)

  const start = useCallback(
    async (input: FreeAdsAuditStartInput) => {
      const userId = user?.id
      // The staged prompt is owned by this person; without an id the chat
      // page would discard it after the quota was spent.
      if (!userId || inFlightRef.current) return
      inFlightRef.current = true
      setError(null)
      setStarting(true)

      let audit: StartAuditResponse
      try {
        audit = await request<StartAuditResponse>({
          url: '/api/free-ads-audit/start',
          method: 'POST',
          body: {
            brandId: input.brandId,
            platform: input.platform,
            ...(input.websiteUrl ? { websiteUrl: input.websiteUrl } : {}),
          },
        })
      } catch (err) {
        setError(freeAdsAuditStartErrorCode(err))
        setStarting(false)
        inFlightRef.current = false
        return
      }

      // The quota is spent: from here on nothing may strand the visitor on
      // this page. Staging comes first because it is what the chat sends.
      usePendingPromptStore
        .getState()
        .setPendingPrompt(
          audit.prompt,
          [],
          undefined,
          newIdempotencyKey('free-ads-audit'),
          {
            type: 'in_product',
            origin: 'free_ads_audit',
            orgSlug: audit.orgSlug,
            brandSlug: audit.brandSlug,
            userId,
          },
        )
      takeFreeAdsAuditRunIntent(userId)
      try {
        const { tokenManager } = await import('@/lib/self-auth/token-manager')
        tokenManager.setActiveOrg(audit.authOrgId)
        await tokenManager.getToken({ forceRefresh: true })
      } catch {
        // The org routes reconcile a member's org from the URL on their own.
      }
      window.location.assign(
        buildBrandUrl(audit.orgSlug, audit.brandSlug, 'chat/new'),
      )
    },
    [request, user],
  )

  return { start, starting, error }
}
